//
// Copyright © 2026 DCM Moguls Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

//
// THIS IS A UI FENCE, NOT A DATA FENCE.
//
// Everything in this file decides what an account is *shown*. It does not decide what an
// account may read or write. Data access is fenced by space membership and by role in the
// transactor, and none of that changes here. An account that is not shown an application can
// still reach that application's documents through any other client, through a direct API
// call, or through a link that resolves to a document rather than to an application. Treat
// `appsByRole` as tidying, never as a security boundary.
//
// Deliberately zero imports: this module is the shared, unit-testable core of the fence and
// every package that needs it already depends on @hcengineering/workbench. Keeping it free of
// imports also lets it be tested standalone, without resolving the workspace graph.
//

/**
 * Application aliases allowed for a role, keyed by role name.
 *
 * Read from the branding file's `appsByRole` key. Role names are matched case-insensitively
 * because the role ladder is not consistently cased at source: `AccountRole.DocGuest` is the
 * string `'DocGuest'` while every other member is upper case (`'OWNER'`, `'MAINTAINER'`,
 * `'USER'`, `'GUEST'`, `'READONLYGUEST'`, `'ADMIN'`). An operator writing `DOCGUEST` in
 * branding.json is doing the obvious thing and must not be silently ignored.
 *
 * @public
 */
export type ApplicationsByRole = Record<string, string[]>

/**
 * Look up one role's entry in the table, case-insensitively.
 *
 * Returns `undefined` when the table is missing, is not an object, or has no entry for this
 * role -- all of which mean "this role is not constrained".
 *
 * @public
 */
export function getRoleAppAllowList (
  role: string | undefined,
  table: ApplicationsByRole | undefined
): string[] | undefined {
  if (role === undefined || role === null || role === '') return undefined
  if (table === undefined || table === null || typeof table !== 'object') return undefined

  const wanted = role.toUpperCase()
  for (const key of Object.keys(table)) {
    if (key.toUpperCase() !== wanted) continue
    const value = table[key]
    // A present-but-malformed entry is treated as "not constrained" rather than as "nothing
    // allowed": a typo in branding.json should not lock an operator out of their own workspace.
    if (!Array.isArray(value)) return undefined
    return value.filter((it) => typeof it === 'string')
  }
  return undefined
}

/**
 * Is `alias` allowed for `role` under `table`?
 *
 * Semantics, in full:
 *  - role absent from the table  -> `true` (unchanged; every other filter still applies)
 *  - role present                -> `true` only if `alias` is listed
 *  - empty array for the role    -> `false` for every alias (that is the operator's stated wish)
 *  - table missing entirely      -> `true`
 *
 * This can only ever remove. It never grants an application that the accessLevel floor, the
 * per-workspace guest fence, or the excluded-applications list has already taken away.
 *
 * @public
 */
export function isAppAllowedByRoleTable (
  alias: string,
  role: string | undefined,
  table: ApplicationsByRole | undefined
): boolean {
  const allowed = getRoleAppAllowList(role, table)
  if (allowed === undefined) return true
  return allowed.includes(alias)
}

/**
 * Intersect a list of already-allowed aliases with the role's entry.
 *
 * Order is the caller's order, not the table's -- the table says *whether*, never *where*.
 * An alias listed for the role but not installed in this deployment simply does not appear,
 * because it was never in `allowedAliases` to begin with.
 *
 * @public
 */
export function filterAliasesByRoleTable (
  role: string | undefined,
  table: ApplicationsByRole | undefined,
  allowedAliases: string[]
): string[] {
  const allowed = getRoleAppAllowList(role, table)
  if (allowed === undefined) return allowedAliases
  return allowedAliases.filter((it) => allowed.includes(it))
}

/**
 * Same intersection, over objects carrying an `alias`.
 *
 * @public
 */
export function filterAppsByRoleTable<T extends { alias: string }> (
  role: string | undefined,
  table: ApplicationsByRole | undefined,
  apps: T[]
): T[] {
  const allowed = getRoleAppAllowList(role, table)
  if (allowed === undefined) return apps
  return apps.filter((it) => allowed.includes(it.alias))
}

/**
 * The application-document form of {@link isAppAllowedByRoleTable}.
 *
 * 🔴 `hidden: true` applications are NEVER fenced by the table, and that is load-bearing.
 * `setting`, `inbox` and `notification` are declared hidden in the model: they are not rail
 * entries and are reached from the account menu and the top of the rail, not from the
 * application list. They are therefore not in the set the table is written about. An operator
 * enumerating the applications they want a role to have in the rail is not saying "and take
 * Settings away" -- but without this rule that is exactly what the first `appsByRole` entry in
 * branding.json would do, to every constrained role at once.
 *
 * `undefined` allows: an alias with no application behind it is not this fence's business.
 *
 * @public
 */
export function isAppDocAllowedByRoleTable (
  app: { alias: string, hidden?: boolean } | undefined,
  role: string | undefined,
  table: ApplicationsByRole | undefined
): boolean {
  if (app === undefined) return true
  if (app.hidden === true) return true
  return isAppAllowedByRoleTable(app.alias, role, table)
}

//
// PER-PERSON VIEW MODES (DCM, dcm.5). Still a UI fence, never a data fence.
//
// The role table above says what a *role* is shown. Modes say what a *person* is shown, so one
// User can have the Simple rail and another the Standard one without changing anybody's role.
// Both live in branding.json next to `appsByRole`:
//
//     "modes":       { "simple": ["chunter", ...], "standard": [...], "full": "*" },
//     "modeByEmail": { "someone@dcmmoguls.com": "standard" }
//
// Resolution, in order -- the first rule that applies wins:
//   0. a person who MAY SWITCH (see canSwitchMode) and has picked a KNOWN mode from the account
//      menu gets that mode -- Owners included, which is what lets an Owner see what staff see;
//   1. an OWNER sees every application, whatever mode they are given;
//   2. a person whose e-mail names a KNOWN mode gets that mode ("*" = everything);
//   3. otherwise the role table decides, exactly as before (unknown mode, malformed mode entry,
//      no mode at all, no modes table at all).
//
// The self-picked mode (rule 0) is stored in the browser, per account. It is offered only to
// the roles in `modeSwitchRoles` (default Owner, Maintainer, Admin), and never to a non-Owner
// whom `modeByEmail` pins to a constrained mode: that pin is the operator's decision, and a
// switch would let the person out of it. A stored pick that is no longer allowed (role
// changed, pin added, mode removed from branding.json) is ignored, never trusted.
// A mode can widen what the person's role table would show -- that is its purpose -- but it can
// never widen past the accessLevel floor, the excluded list or the per-workspace guest fence,
// because every caller applies those first and this only filters what is left.
//

/**
 * One mode: an allow-list of application aliases, or `'*'` for every application.
 *
 * @public
 */
export type ApplicationMode = string[] | '*'

/**
 * The per-person half of the fence, as read from branding.json.
 *
 * @public
 */
export interface ApplicationModes {
  modes?: Record<string, ApplicationMode>
  modeByEmail?: Record<string, string>
  /**
   * Roles offered the "View as" switch in the account menu. Absent or malformed = the default,
   * {@link DEFAULT_MODE_SWITCH_ROLES}; an empty array turns the switch off for everybody.
   */
  modeSwitchRoles?: string[]
}

/** Roles offered the mode switch when branding.json does not say. @public */
export const DEFAULT_MODE_SWITCH_ROLES: readonly string[] = ['OWNER', 'MAINTAINER', 'ADMIN']

/**
 * The account facts the fence needs: its role in this workspace and its e-mail addresses.
 *
 * @public
 */
export interface AppAccessSubject {
  role?: string
  emails?: string[]
}

/** Roles that always see every application. @public */
export const FULL_ACCESS_ROLES: readonly string[] = ['OWNER']

function lookupCaseInsensitive<T> (table: Record<string, T> | undefined | null, key: string): T | undefined {
  if (table === undefined || table === null || typeof table !== 'object') return undefined
  const wanted = key.trim().toLowerCase()
  for (const k of Object.keys(table)) {
    if (k.trim().toLowerCase() === wanted) return table[k]
  }
  return undefined
}

/**
 * The mode named for the first of `emails` that has one, or `undefined`. E-mails and mode
 * names match case-insensitively: account e-mails are stored as typed, operators type lower case.
 *
 * @public
 */
export function getModeNameForEmails (
  emails: string[] | undefined,
  table: ApplicationModes | undefined
): string | undefined {
  if (emails === undefined || emails === null || !Array.isArray(emails)) return undefined
  for (const email of emails) {
    if (typeof email !== 'string' || email.trim() === '') continue
    const mode = lookupCaseInsensitive(table?.modeByEmail, email)
    if (typeof mode === 'string' && mode.trim() !== '') return mode
  }
  return undefined
}

/**
 * A mode's allow-list: `null` = every application, an array = only those, `undefined` = no such
 * mode (or a malformed entry), which sends the caller back to the role table.
 *
 * @public
 */
export function getModeAppAllowList (
  mode: string | undefined,
  table: ApplicationModes | undefined
): string[] | null | undefined {
  if (mode === undefined) return undefined
  const entry = lookupCaseInsensitive<unknown>(table?.modes, mode)
  if (entry === '*') return null
  if (!Array.isArray(entry)) return undefined
  return entry.filter((it): it is string => typeof it === 'string')
}

/**
 * The one question every call site asks: which aliases is this person shown?
 * `undefined` = not constrained (every application the other filters allow).
 *
 * @public
 */
export function resolveAppAllowList (
  subject: AppAccessSubject | undefined,
  roleTable: ApplicationsByRole | undefined,
  modes: ApplicationModes | undefined,
  pickedMode?: string
): string[] | undefined {
  const picked = getAllowedPickedMode(subject, modes, pickedMode)
  if (picked !== undefined) {
    const pickedList = getModeAppAllowList(picked, modes)
    if (pickedList === null) return undefined
    if (pickedList !== undefined) return pickedList
  }
  const role = subject?.role
  if (typeof role === 'string' && FULL_ACCESS_ROLES.includes(role.toUpperCase())) return undefined
  const modeList = getModeAppAllowList(getModeNameForEmails(subject?.emails, modes), modes)
  if (modeList === null) return undefined
  if (modeList !== undefined) return modeList
  return getRoleAppAllowList(role, roleTable)
}

/**
 * The names of the modes defined in branding.json, in file order, skipping malformed entries.
 * These are what the "View as" menu offers.
 *
 * @public
 */
export function getModeNames (table: ApplicationModes | undefined): string[] {
  const modes = table?.modes
  if (modes === undefined || modes === null || typeof modes !== 'object') return []
  return Object.keys(modes).filter((name) => getModeAppAllowList(name, table) !== undefined)
}

/**
 * May this person pick their own mode from the account menu?
 *
 *  - only roles in `modeSwitchRoles` (default {@link DEFAULT_MODE_SWITCH_ROLES});
 *  - never when no mode is defined (there is nothing to pick);
 *  - never a non-Owner whom `modeByEmail` pins to anything but a `'*'` mode. The pin is the
 *    operator's decision about that person; a switch would be a way out of it. Picking a
 *    narrower view than one's own is harmless, so a person pinned to `'*'` keeps the switch.
 *
 * Still a UI fence: a person who passes this sees more or fewer rail entries, nothing else.
 *
 * @public
 */
export function canSwitchMode (subject: AppAccessSubject | undefined, table: ApplicationModes | undefined): boolean {
  const role = subject?.role
  if (typeof role !== 'string' || role === '') return false
  if (getModeNames(table).length === 0) return false
  const configured = table?.modeSwitchRoles
  const roles = Array.isArray(configured)
    ? configured.filter((it): it is string => typeof it === 'string')
    : DEFAULT_MODE_SWITCH_ROLES
  const wanted = role.toUpperCase()
  if (!roles.some((it) => it.trim().toUpperCase() === wanted)) return false
  if (FULL_ACCESS_ROLES.includes(wanted)) return true
  const pinned = getModeNameForEmails(subject?.emails, table)
  if (pinned === undefined) return true
  return getModeAppAllowList(pinned, table) === null
}

/**
 * The picked mode, if it may be honoured: the person may switch and the mode exists. Returns
 * the mode's name as written in branding.json, or `undefined` (= ignore the pick).
 *
 * @public
 */
export function getAllowedPickedMode (
  subject: AppAccessSubject | undefined,
  table: ApplicationModes | undefined,
  pickedMode: string | undefined
): string | undefined {
  if (typeof pickedMode !== 'string' || pickedMode.trim() === '') return undefined
  if (!canSwitchMode(subject, table)) return undefined
  const wanted = pickedMode.trim().toLowerCase()
  return getModeNames(table).find((name) => name.trim().toLowerCase() === wanted)
}

/** @public */
export function isAppAllowedForSubject (
  alias: string,
  subject: AppAccessSubject | undefined,
  roleTable: ApplicationsByRole | undefined,
  modes: ApplicationModes | undefined,
  pickedMode?: string
): boolean {
  const allowed = resolveAppAllowList(subject, roleTable, modes, pickedMode)
  return allowed === undefined || allowed.includes(alias)
}

/** @public */
export function filterAppsForSubject<T extends { alias: string }> (
  subject: AppAccessSubject | undefined,
  roleTable: ApplicationsByRole | undefined,
  modes: ApplicationModes | undefined,
  apps: T[],
  pickedMode?: string
): T[] {
  const allowed = resolveAppAllowList(subject, roleTable, modes, pickedMode)
  if (allowed === undefined) return apps
  return apps.filter((it) => allowed.includes(it.alias))
}

/**
 * The application-document form. `hidden: true` applications are never fenced -- the same
 * load-bearing rule as {@link isAppDocAllowedByRoleTable}, for the same reason: Settings, Inbox
 * and Notifications are not rail entries and no mode is written about them.
 *
 * @public
 */
export function isAppDocAllowedForSubject (
  app: { alias: string, hidden?: boolean } | undefined,
  subject: AppAccessSubject | undefined,
  roleTable: ApplicationsByRole | undefined,
  modes: ApplicationModes | undefined,
  pickedMode?: string
): boolean {
  if (app === undefined) return true
  if (app.hidden === true) return true
  return isAppAllowedForSubject(app.alias, subject, roleTable, modes, pickedMode)
}
